import React, { useEffect, useState, useRef, useContext } from "react";
import { HeaderAction } from "../components/HeaderAction";
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Menu,
  Select,
  Title,
  Tooltip,
} from "@mantine/core";
import { TaskDto, TaskState, TaskType, UserDto } from "../dto/user";
import { useApi } from "../useApi";
import { getUsersActivatedMembers, getTeams } from "../apiRoutes";
import moment, { Moment } from "moment";
import Legend from "../components/calendar/Legend";
import DayCell from "../components/DayCell";
import { capitalizeFirstLetter, generateDays } from "../utils";
import { IconChevronLeft, IconPencil, IconPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import TaskForm from "../components/TaskForm";
import { IconChevronRight } from "@tabler/icons-react";
import DayCellTitle from "../components/DayCellTitle";
import { UserContext } from "../UserContext";
import InOfficeDayModal from "../components/InOfficeDayModal";
import { useTasks } from "../contexts/TasksContext";
import { InOfficeDay } from "../types";

const UserHeader = React.memo(
  ({
    user,
    pastTasks = [],
  }: {
    user: {
      label: string;
      value: string;
      places: string;
    };
    pastTasks?: TaskDto[];
  }) => {
    // Filtrer les tâches bleues (recordingWriting) et jaunes (meetingWriting)
    const relevantTasks = pastTasks.filter(
      (task) =>
        task.type === TaskType.recordingWriting ||
        task.type === TaskType.meetingWriting
    );

    // Statuts considérés comme "validés"
    const validatedStates = [
      TaskState.validatedByRedactor,
      TaskState.delivered,
      TaskState.billed,
    ];

    // Filtrer les tâches non validées
    const unvalidatedTasks = relevantTasks.filter(
      (task) => !task.state || !validatedStates.includes(task.state)
    );
    const hasUnvalidatedTasks = unvalidatedTasks.length > 0;

    // Tooltip avec la liste des tâches non validées
    const tooltipContent = unvalidatedTasks
      .map((task) => {
        const startDate = task.startDate
          ? new Date(task.startDate).toLocaleDateString("fr-FR")
          : "";
        return `${startDate} - ${task.customer?.code ?? "?"}`;
      })
      .join("\n");

    return (
      <div
        style={{
          position: "relative",
          width: 100,
          minWidth: 100,
          textAlign: "center",
          padding: "15px 0",
          top: 0,
          background: "white",
          fontWeight: 500,
          zIndex: 2,
          minHeight: 82,
          whiteSpace: "nowrap",
          overflow: "visible",
        }}
      >
        {/* Alerte jaune si des comptes rendus de la veille ne sont pas validés */}
        {hasUnvalidatedTasks && (
          <Tooltip
            label={tooltipContent}
            multiline
            withinPortal
            style={{ whiteSpace: "pre-line" }}
          >
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#f5a623",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        )}
        <div>{user.label}</div>
        <div>{user.places ? `(${user.places})` : ""}</div>
      </div>
    );
  }
);

const getNextWeekday = (date: Moment): Moment => {
  let nextDate = date.clone();
  while (nextDate.day() === 0 || nextDate.day() === 6) {
    nextDate = nextDate.add(1, "day");
  }
  return nextDate;
};

const Planning = () => {
  const { tasks, refreshTasks } = useTasks();
  const [days, setDays] = useState<Moment[]>([]);
  const [redactors, setRedactors] = useState<
    { label: string; value: string; places: string; teamId?: number }[]
  >([]);
  const [selectedRedactor, setSelectedRedactor] = useState<string | null>(null);
  const [teams, setTeams] = useState<{ value: string; label: string }[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const api = useApi();
  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];
  const navigation = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const [refreshCounter, setRefreshCounter] = React.useState(0);
  const [currentMonth, setCurrentMonth] = useState(moment());
  const dateRefs = useRef<{ [key: string]: HTMLTableRowElement }>({});
  const [isScrollEnabled, setIsScrollEnabled] = useState(true);
  const { user } = useContext(UserContext);
  const [
    inOfficeDayModalOpened,
    { open: openInOfficeDayModal, close: closeInOfficeDayModal },
  ] = useDisclosure(false);
  const [inOfficeDays, setInOfficeDays] = useState<{
    [key: string]: InOfficeDay[];
  }>({});
  const [selectedDayType, setSelectedDayType] = useState<
    "withoutpresence" | "coworking" | "office"
  >("withoutpresence");

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsScrollEnabled(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isScrollEnabled) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = "visible"; // Enable scrolling
    }

    return () => {
      document.body.style.overflow = "visible"; // Re-enable scrolling on cleanup
    };
  }, [isScrollEnabled]);

  React.useEffect(() => {
    const fetchUsers = async () => {
      const response: UserDto[] = await getUsersActivatedMembers(api);
      const redactorsRaw = response.map((user) => ({
        label: user?.code ?? "",
        places: user?.places ?? "",
        value: user.id?.toString() ?? "",
        teamId: user.teamId,
      }));
      setRedactors(redactorsRaw);
    };
    const fetchTeams = async () => {
      try {
        const teamsData = await getTeams(api);
        setTeams(
          teamsData.map((team: { id: number; name: string }) => ({
            value: team.id.toString(),
            label: team.name,
          }))
        );
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };
    fetchUsers();
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    refreshTasks();
  }, [refreshTasks, refreshCounter]);

  useEffect(() => {
    const fetchInOfficeDays = async () => {
      try {
        console.log('api.get("/inofficedates");', refreshCounter);
        const response = await api.get("/inofficedates");
        const daysMap: { [key: string]: InOfficeDay[] } = {};
        response.data.forEach((day: any) => {
          if (!daysMap[day.userId]) {
            daysMap[day.userId] = [];
          }
          daysMap[day.userId].push({
            date: day.date.split("T")[0],
            type: day.type,
          });
        });
        setInOfficeDays(daysMap);
      } catch (error) {
        console.error("Error fetching inOfficeDays:", error);
      }
    };
    fetchInOfficeDays();
  }, [api, refreshCounter]);

  // Fonction pour trouver l'inOfficeDay correspondant à un jour spécifique pour un redacteur
  const findInOfficeDayForDateAndRedactor = (
    dayMoment: Moment,
    redactorId: string
  ) => {
    if (!inOfficeDays[redactorId]) return undefined;
    const dayStr = dayMoment.format("YYYY-MM-DD");
    return inOfficeDays[redactorId].find((day) => day.date === dayStr);
  };

  // @ts-ignore
  const filterTasksForDay = (day) => {
    const dayStr = day.format("YYYY-MM-DD");
    return tasks.filter((task) => {
      // @ts-ignore
      const startDateStr = task?.startDate?.split("T")[0];
      // @ts-ignore
      const endDateStr = task?.endDate?.split("T")[0] || startDateStr;
      return startDateStr <= dayStr && dayStr <= endDateStr;
    });
  };

  useEffect(() => {
    setDays(generateDays(currentMonth));
  }, [currentMonth]);

  useEffect(() => {
    let currentDay = moment(today);
    if (currentDay.day() === 0 || currentDay.day() === 6) {
      currentDay = getNextWeekday(currentDay);
    }
    const currentDayKey = currentDay.format("YYYY-MM-DD");
    if (dateRefs.current[currentDayKey]) {
      dateRefs.current[currentDayKey].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const filterTasksForRedactor = (
    tasksForDay: TaskDto[],
    redactorId: number
  ) => {
    return tasksForDay.filter((task) => task.redactorId === redactorId);
  };

  // Obtenir les tâches passées (2 dernières semaines) pour un rédacteur
  const getPastTasksForRedactor = (redactorId: number) => {
    const today = moment().startOf("day");
    const twoWeeksAgo = moment().subtract(2, "weeks").startOf("day");
    return tasks.filter((task) => {
      if (task.redactorId !== redactorId) return false;
      if (!task.endDate) return false;
      const endDate = moment(task.endDate).startOf("day");
      return endDate.isBefore(today) && endDate.isAfter(twoWeeksAgo);
    });
  };

  return (
    <Box bg={"#F9FAFB"} pb={1}>
      <HeaderAction planning />
      <TaskForm
        opened={opened}
        open={open}
        close={close}
        isPlanning={true}
        isRecordings={false}
        api={api}
        setRefreshCounter={setRefreshCounter}
        // users={users}
        redactors={redactors}
        // setIsPlanning={setIsPlanning}
        tasks={tasks}
      />
      <InOfficeDayModal
        opened={inOfficeDayModalOpened}
        close={closeInOfficeDayModal}
        redactors={redactors}
        initialType={selectedDayType}
        onSuccess={() => {
          console.log("onSuccess increase refresh");
          setRefreshCounter((prev) => prev + 1);
        }}
      />
      <Box
        style={{
          backgroundColor: "white",
          border: "1px solid #EAECF0",
          borderTopRightRadius: 12,
          borderTopLeftRadius: 12,
        }}
        mr={16}
        ml={16}
        mt={16}
        p={24}
      >
        <Flex justify="space-between">
          <Flex gap={10}>
            <ActionIcon
              variant="default"
              onClick={() =>
                setCurrentMonth(currentMonth.clone().subtract(1, "months"))
              }
            >
              <IconChevronLeft size="1rem" />
            </ActionIcon>
            <Title mb={5} mt={2}>
              Planning {capitalizeFirstLetter(currentMonth.format("MMMM YYYY"))}
            </Title>
            <ActionIcon
              variant="default"
              onClick={() =>
                setCurrentMonth(currentMonth.clone().add(1, "months"))
              }
            >
              <IconChevronRight size="1rem" />
            </ActionIcon>
          </Flex>
          <Flex gap={20}>
            <Select
              data={teams}
              placeholder="Équipe"
              value={selectedTeam}
              onChange={(value) => {
                setSelectedTeam(value);
                setSelectedRedactor(null);
              }}
              clearable
              searchable
              style={{ minWidth: 120 }}
            />
            <Select
              data={redactors.filter(
                (r) => !selectedTeam || r.teamId?.toString() === selectedTeam
              )}
              placeholder="Rédacteurs"
              onChange={(value) => {
                console.log(value);
                setSelectedRedactor(value);
              }}
              clearable
              searchable
            />
            <Menu trigger="hover" openDelay={100} closeDelay={400}>
              {user?.role === "administrateur" && (
                <Menu.Target>
                  <Button
                    leftIcon={<IconPlus size={18} />}
                    style={{ height: 36 }}
                  >
                    Créer
                  </Button>
                </Menu.Target>
              )}
              <Menu.Dropdown>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => {
                    const createBreak = new CustomEvent("planTask", {
                      detail: { taskId: 0, type: TaskType.break },
                    });
                    window.dispatchEvent(createBreak);
                  }}
                >
                  Absence
                </Menu.Item>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => {
                    const createBreak = new CustomEvent("planTask", {
                      detail: { taskId: 0, type: TaskType.otherFlexible },
                    });
                    window.dispatchEvent(createBreak);
                  }}
                >
                  Autre Flexible
                </Menu.Item>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => {
                    const createBreak = new CustomEvent("planTask", {
                      detail: { taskId: 0, type: TaskType.otherFixed },
                    });
                    window.dispatchEvent(createBreak);
                  }}
                >
                  Autre Fixé
                </Menu.Item>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => navigation("/reunions")}
                >
                  Enregistrement
                </Menu.Item>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => navigation("/enregistrements")}
                >
                  Réunion
                </Menu.Item>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => {
                    setSelectedDayType("withoutpresence");
                    openInOfficeDayModal();
                  }}
                >
                  Journée sans présence
                </Menu.Item>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => {
                    setSelectedDayType("coworking");
                    openInOfficeDayModal();
                  }}
                >
                  Journée de coworking
                </Menu.Item>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => {
                    setSelectedDayType("office");
                    openInOfficeDayModal();
                  }}
                >
                  Journée au bureau
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
        <Legend />
      </Box>
      <div
        style={{
          marginRight: 16,
          marginLeft: 16,
          position: "relative",
          marginBottom: 16,
        }}
      >
        {!selectedRedactor && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 102,
              height: 84,
              backgroundColor: "white",
              zIndex: 3,
              border: "1px solid #EAECF0",
            }}
          />
        )}
        <div
          style={{
            borderCollapse: "collapse",
            width: "100%",
            overflow: "auto",
            height: "calc(100vh - 223px)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[
                  { label: "", value: "", places: "", teamId: undefined },
                  ...redactors.filter(
                    (u) =>
                      (u.value === selectedRedactor || !selectedRedactor) &&
                      (!selectedTeam || u.teamId?.toString() === selectedTeam)
                  ),
                ].map((user, index) => (
                  <th
                    key={index}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "white",
                      minWidth: 100,
                      padding: 0,
                    }}
                  >
                    <div
                      style={{
                        border: "1px solid #EAECF0",
                      }}
                    >
                      <UserHeader
                        key={index}
                        user={user}
                        pastTasks={
                          user.value
                            ? getPastTasksForRedactor(+user.value)
                            : []
                        }
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((day, rowIndex) => {
                const dayStr = day.format("YYYY-MM-DD");
                const tasksForDay = filterTasksForDay(day);
                return (
                  <tr
                    key={rowIndex}
                    style={{ position: "relative" }}
                    ref={(el) => {
                      if (el && isScrollEnabled) {
                        dateRefs.current[dayStr] = el;
                        if (dayStr === moment(today).format("YYYY-MM-DD")) {
                          const invisibleAnchor = document.createElement("div");
                          invisibleAnchor.style.position = "absolute";
                          invisibleAnchor.style.top = "-82px";
                          invisibleAnchor.style.width = "1px";
                          invisibleAnchor.style.height = "1px";
                          el.firstElementChild?.appendChild(invisibleAnchor);
                          invisibleAnchor.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }
                    }}
                  >
                    {[
                      { label: "", value: "", places: "", teamId: undefined },
                      ...redactors.filter(
                        (u) =>
                          (u.value === selectedRedactor || !selectedRedactor) &&
                          (!selectedTeam || u.teamId?.toString() === selectedTeam)
                      ),
                    ].map((user, colIndex) => {
                      const inOfficeDayForCell =
                        colIndex > 0
                          ? findInOfficeDayForDateAndRedactor(day, user.value)
                          : undefined;

                      return (
                        <td
                          key={colIndex}
                          style={{
                            position: colIndex === 0 ? "sticky" : undefined,
                            left: colIndex === 0 ? 0 : undefined,
                            zIndex: colIndex === 0 ? 2 : undefined,
                            backgroundColor: "white",
                            minWidth: 100,
                            padding: 0,
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #EAECF0",
                              height: "100%",
                            }}
                          >
                            {colIndex === 0 ? (
                              <DayCellTitle
                                day={day}
                                formattedToday={formattedToday}
                                key={colIndex}
                                tasks={tasksForDay}
                              />
                            ) : (
                              <DayCell
                                day={day}
                                tasks={filterTasksForRedactor(
                                  tasksForDay,
                                  +user.value
                                )}
                                inOfficeDay={inOfficeDayForCell}
                                redactorId={user?.value}
                                setRefresh={setRefreshCounter}
                              />
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Box>
  );
};

export default Planning;
